import { useContext, useEffect, useMemo, useState } from 'react'
import ErrorBoundary from '../../components/ErrorBoundary'
import { getSettingsAdminInfo, getSettingsIndexConfig } from '../../services/settings-config'
import { openSupportForm } from '../../services/support'
import { userHasGroup } from "../../services/utils"

import { formatPhoneNumber, getScoutContext, updateScoutAdminSettings } from '../../services/scout'
import {
    AlternateEmailOutlined as AlternateEmailOutlinedIcon,
    BadgeOutlined as BadgeOutlinedIcon,
    ChevronRight as ChevronRightIcon,
    CreditCardOutlined as CreditCardOutlinedIcon,
    ContactPhoneOutlined as ContactPhoneOutlinedIcon,
    Inventory2Outlined as Inventory2OutlinedIcon,
    PhoneIphoneOutlined as PhoneIphoneOutlinedIcon,
    SettingsSuggestOutlined as SettingsSuggestOutlinedIcon,
    VisibilityOutlined as VisibilityOutlinedIcon,
    EditOutlined as EditOutlinedIcon,
    CheckOutlined as CheckOutlinedIcon,
    CloseOutlined as CloseOutlinedIcon,
    BusinessOutlined as BusinessOutlinedIcon,
    AdminPanelSettings as AdminPanelSettingsIcon,
    AssessmentOutlined as AssessmentOutlinedIcon,
    CategoryOutlined as CategoryOutlinedIcon,
} from '@mui/icons-material'

import { emitSnackbar } from '../../lib/snackbar-emitter'
import { settingsContext } from '.'

const getEditableDisplayValue = ({ value, type }) => {
    if (type === 'phone') {
        return formatPhoneNumber(value)
    }

    return value || ''
}

const EditableAdminInfoRow = ({ icon, label, field, value, type = 'text', onSave }) => {
    const [isEditing, setIsEditing] = useState(false)
    const [internalValue, setInternalValue] = useState(value || '')
    const [isSaving, setIsSaving] = useState(false)

    useEffect(() => {
        if (!isEditing) {
            setInternalValue(value || '')
        }
    }, [value, isEditing])

    const handleCancel = () => {
        setInternalValue(value || '')
        setIsEditing(false)
    }

    const handleSave = async () => {
        const nextValue = String(internalValue || '').trim()
        const currentValue = String(value || '').trim()

        if (nextValue === currentValue) {
            setIsEditing(false)
            return
        }

        setIsSaving(true)
        try {
            await onSave(field, nextValue)
            setIsEditing(false)
        } finally {
            setIsSaving(false)
        }
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            handleSave()
        }

        if (event.key === 'Escape') {
            event.preventDefault()
            handleCancel()
        }
    }

    return (
        <div className="scout-settings-info-row scout-settings-info-row--editable">
            {icon}
            <div>
                <span>{label}</span>
                <div className="scout-settings-editable-field">
                    <input
                        type={type === 'email' ? 'email' : 'text'}
                        value={isEditing ? internalValue : getEditableDisplayValue({ value, type })}
                        disabled={!isEditing || isSaving}
                        onChange={(event) => setInternalValue(event.target.value)}
                        onKeyDown={handleKeyDown}
                        className="scout-settings-editable-field__input"
                    />
                    {isEditing ? (
                        <div className="scout-settings-editable-field__actions">
                            <button
                                type="button"
                                className="scout-settings-editable-field__button scout-settings-editable-field__button--save"
                                onClick={handleSave}
                                disabled={isSaving}
                                aria-label={`Salvar ${label}`}
                            >
                                <CheckOutlinedIcon fontSize="small" />
                            </button>
                            <button
                                type="button"
                                className="scout-settings-editable-field__button"
                                onClick={handleCancel}
                                disabled={isSaving}
                                aria-label={`Cancelar edição de ${label}`}
                            >
                                <CloseOutlinedIcon fontSize="small" />
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="scout-settings-editable-field__button"
                            onClick={() => setIsEditing(true)}
                            aria-label={`Editar ${label}`}
                        >
                            <EditOutlinedIcon fontSize="small" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}

const getSettingsCardIcon = (id) => {
    switch (id) {
        case "phones": return <ContactPhoneOutlinedIcon />
        case "admins": return <AdminPanelSettingsIcon />
        case "payments": return <CreditCardOutlinedIcon />
        case "restricted": return <VisibilityOutlinedIcon />
        case "reports": return <AssessmentOutlinedIcon />
        case "categories": return <CategoryOutlinedIcon />
        default: return <Inventory2OutlinedIcon />
    }
}

const SettingsBase = () => {

    const { changeLocation } = useContext(settingsContext)
    const { user } = getScoutContext()
    const [adminInfo, setAdminInfo] = useState(() => getSettingsAdminInfo(user))
    const indexConfig = useMemo(() => getSettingsIndexConfig(), [])

    const handleAdminInfoSave = async (field, value) => {
        try {
            const response = await updateScoutAdminSettings({ [field]: value })
            if (response?.user) {
                setAdminInfo(getSettingsAdminInfo(response.user))
            } else {
                setAdminInfo((current) => ({ ...current, [field === 'telephone' ? 'phone' : field]: value }))
            }
            emitSnackbar({ message: 'Dados atualizados com sucesso.', severity: 'success' })
        } catch (error) {
            emitSnackbar({ message: error?.message || 'Não foi possível atualizar os dados.', severity: 'error' })
            throw error
        }
    }

    return (
        <>
            <section className="scout-settings-section">
                <div className="scout-settings-section__label">
                    <BadgeOutlinedIcon />
                    <span>{'Dados do administrador'}</span>
                </div>

                <div className="scout-settings-card scout-settings-card--stacked">
                    <EditableAdminInfoRow
                        icon={<BadgeOutlinedIcon />}
                        label="Nome"
                        field="name"
                        value={adminInfo.name}
                        onSave={handleAdminInfoSave}
                    />
                    <EditableAdminInfoRow
                        icon={<AlternateEmailOutlinedIcon />}
                        label="E-mail"
                        field="email"
                        value={adminInfo.email}
                        type="email"
                        onSave={handleAdminInfoSave}
                    />
                    <EditableAdminInfoRow
                        icon={<PhoneIphoneOutlinedIcon />}
                        label="Telefone"
                        field="telephone"
                        value={adminInfo.phone}
                        type="phone"
                        onSave={handleAdminInfoSave}
                    />
                    <EditableAdminInfoRow
                        icon={<BusinessOutlinedIcon />}
                        label="Empresa"
                        field="company"
                        value={adminInfo.company}
                        onSave={handleAdminInfoSave}
                    />
                </div>
            </section>

            <section className="scout-settings-section">
                <div className="scout-settings-section__label">
                    <SettingsSuggestOutlinedIcon />
                    <span>{indexConfig?.otherSectionLabel || 'Outras configurações'}</span>
                </div>

                <div className="scout-settings-card scout-settings-card--menu">
                    {(indexConfig?.cards || []).map((item) => (
                        userHasGroup(item.groups) && (
                            <button key={item.id} type="button" className="scout-settings-menu-row" onClick={() => changeLocation(item.path)}>
                                {getSettingsCardIcon(item.id)}
                                <span>{item.label}</span>
                                <ChevronRightIcon />
                            </button>
                        )
                    ))}
                </div>
            </section>
        </>
    )
}

const SettingsBaseWithBoundary = (props) => (
    <ErrorBoundary
        componentName="SettingsBase"
        showReload
        logToBackend
        title="Erro nas configurações"
        message="Ocorreu um erro ao carregar as configurações. Tente recarregar."
    >
        <SettingsBase {...props} />
    </ErrorBoundary>
)

export default SettingsBaseWithBoundary